import React, {useEffect, useMemo, useState} from "react";
import {
    Alert,
    Box,
    Card,
    CardContent,
    Chip,
    Container,
    Divider,
    Skeleton,
    Slider,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material";
import {getCashflowForecast} from "../../api";
import {hasAccountingAccess} from "../../subscription";
import UpgradeNotice from "../shared/UpgradeNotice";
import {useAuth} from "../auth/AuthContext";
import {logError} from "../../logging/loggingService";

const fmtCurrency = (value) => new Intl.NumberFormat("de-DE", {style: "currency", currency: "EUR"}).format(Number(value ?? 0));
const fmtMonth = (isoDate) => {
    if (!isoDate) {
        return "";
    }
    const date = new Date(isoDate);
    return date.toLocaleDateString("de-DE", {month: "short", year: "numeric"});
};

function buildSeries(points = [], startingBalance = 0) {
    let balance = Number(startingBalance ?? 0);
    return points.map((point) => {
        const net = Number(point?.net ?? 0);
        balance += net;
        return {
            period: point?.period,
            label: fmtMonth(point?.period),
            balance,
        };
    });
}

function buildAdjustedSeries(points = [], startingBalance = 0, adjustment = 0) {
    let balance = Number(startingBalance ?? 0);
    return points.map((point) => {
        const net = Number(point?.net ?? 0) + Number(adjustment ?? 0);
        balance += net;
        return {
            period: point?.period,
            label: fmtMonth(point?.period),
            balance,
        };
    });
}

function CashflowChart({baseline, adjusted, loading}) {
    if (loading) {
        return <Skeleton variant="rectangular" height={260} sx={{borderRadius: 2}}/>;
    }

    const baselineSeries = baseline ?? [];
    const adjustedSeries = adjusted ?? [];
    const reference = adjustedSeries.length ? adjustedSeries : baselineSeries;

    if (!reference.length) {
        return (
            <Box sx={{height: 260, display: "flex", alignItems: "center", justifyContent: "center", color: "text.secondary"}}>
                <Typography variant="body2">Keine Daten verfügbar.</Typography>
            </Box>
        );
    }

    const allValues = [...baselineSeries.map((p) => p.balance), ...adjustedSeries.map((p) => p.balance)];
    const min = Math.min(...allValues);
    const max = Math.max(...allValues);
    const padding = Math.max((max - min) * 0.15, 100);
    const yMin = min - padding;
    const yMax = max + padding;
    const xStep = 100 / Math.max(1, reference.length - 1);
    const scaleY = (value) => 100 - ((value - yMin) / (yMax - yMin)) * 100;

    const pathFor = (series) =>
        series
            .map((point, index) => `${index === 0 ? "M" : "L"}${index * xStep},${scaleY(point.balance)}`)
            .join(" ");

    const horizontalLines = Array.from({length: 4}, (_, idx) => ((idx + 1) * 20));

    return (
        <Box sx={{width: "100%", height: 260}}>
            <svg viewBox="0 0 100 100" preserveAspectRatio="none" width="100%" height="100%">
                <rect x="0" y="0" width="100" height="100" fill="none" stroke="#d0d7de" strokeWidth="0.6"/>
                {horizontalLines.map((y) => (
                    <line key={y} x1="0" x2="100" y1={y} y2={y} stroke="#e5e7eb" strokeWidth="0.4"/>
                ))}
                {baselineSeries.length > 0 && (
                    <path d={pathFor(baselineSeries)} fill="none" stroke="#1976d2" strokeWidth="1.8" strokeLinecap="round"/>
                )}
                {adjustedSeries.length > 0 && (
                    <path
                        d={pathFor(adjustedSeries)}
                        fill="none"
                        stroke="#ff9800"
                        strokeWidth="1.8"
                        strokeDasharray="3 2"
                        strokeLinecap="round"
                    />
                )}
            </svg>
        </Box>
    );
}

export default function CashflowForecast() {
    const {user} = useAuth();
    const hasAccounting = hasAccountingAccess(user?.paymentPlan);
    const [scenario, setScenario] = useState("baseline");
    const [forecast, setForecast] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [adjustment, setAdjustment] = useState(0);

    useEffect(() => {
        if (!hasAccounting) {
            return;
        }
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const result = await getCashflowForecast(scenario);
                if (!result || result.status >= 400) {
                    throw new Error(`Unexpected response status ${result?.status}`);
                }
                if (cancelled) {
                    return;
                }
                const data = result?.data ?? null;
                setForecast(data);
                const defaultAdjustment = Number(data?.assumptions?.sliderDefault ?? 0);
                setAdjustment(defaultAdjustment);
            } catch (err) {
                if (!cancelled) {
                    setError("Die Prognose konnte nicht geladen werden.");
                    logError("CashflowForecast", err, {component: "CashflowForecast"});
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [scenario, hasAccounting]);

    const sliderConfig = useMemo(() => {
        const assumptions = forecast?.assumptions ?? {};
        const min = Number(assumptions.sliderMin ?? -2000);
        const max = Number(assumptions.sliderMax ?? 2000);
        const step = Number(assumptions.sliderStep ?? 100);
        return {min, max, step};
    }, [forecast]);

    const baselineSeries = useMemo(() => buildSeries(forecast?.points, forecast?.startingBalance), [forecast]);
    const adjustedSeries = useMemo(
        () => buildAdjustedSeries(forecast?.points, forecast?.startingBalance, adjustment),
        [forecast, adjustment]
    );

    const endBalanceBaseline = baselineSeries.length
        ? baselineSeries[baselineSeries.length - 1].balance
        : Number(forecast?.startingBalance ?? 0);
    const endBalanceAdjusted = adjustedSeries.length
        ? adjustedSeries[adjustedSeries.length - 1].balance
        : endBalanceBaseline;

    const assumptions = forecast?.assumptions ?? {};
    const alerts = forecast?.alerts ?? [];
    const scenarios = forecast?.availableScenarios ?? [];

    const handleScenarioChange = (_event, value) => {
        if (!value) {
            return;
        }
        setScenario(value);
    };

    if (!hasAccounting) {
        return <UpgradeNotice title="Liquiditätsprognose" description="Diese Funktion ist im Buchhaltungstarif enthalten."/>;
    }

    return (
        <Container maxWidth="lg" sx={{pb: 6}}>
            <Typography variant="h4" sx={{mb: 1}}>
                Liquiditätsprognose
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{mb: 3}}>
                Modellierung auf Basis historischer Zahlungsströme, erwarteter Abonnements und offener Posten.
            </Typography>

            {error && (
                <Alert severity="error" sx={{mb: 3}}>
                    {error}
                </Alert>
            )}

            <Stack spacing={3}>
                <Card sx={{borderRadius: 3}}>
                    <CardContent>
                        <Stack spacing={2}>
                            <Box>
                                <Typography variant="h6">Szenario auswählen</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {forecast?.scenarioDescription ?? ""}
                                </Typography>
                                <ToggleButtonGroup
                                    exclusive
                                    color="primary"
                                    value={scenario}
                                    onChange={handleScenarioChange}
                                    sx={{mt: 2, flexWrap: "wrap"}}
                                >
                                    {(scenarios.length ? scenarios : [
                                        {key: "baseline", label: "Baseline"},
                                        {key: "optimistic", label: "Optimistisch"},
                                        {key: "pessimistic", label: "Pessimistisch"},
                                    ]).map((option) => (
                                        <ToggleButton key={option.key} value={option.key} sx={{textTransform: "none", mr: 1, mb: 1}}>
                                            {option.label}
                                        </ToggleButton>
                                    ))}
                                </ToggleButtonGroup>
                            </Box>
                            <Divider/>
                            <Box>
                                <Typography variant="h6">What-if-Simulation</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
                                    Wie verändert sich der Cashflow, wenn sich der monatliche Saldo um einen konstanten Betrag verschiebt?
                                </Typography>
                                <Slider
                                    min={sliderConfig.min}
                                    max={sliderConfig.max}
                                    step={sliderConfig.step}
                                    value={adjustment}
                                    onChange={(_event, value) => setAdjustment(Array.isArray(value) ? value[0] : value)}
                                    valueLabelDisplay="auto"
                                    valueLabelFormat={(value) => fmtCurrency(value)}
                                />
                                <Stack direction="row" spacing={2} sx={{mt: 1}}>
                                    <Chip label={`Annahme: ${fmtCurrency(adjustment)} pro Monat`} color="primary" variant="outlined"/>
                                    <Chip label={`Basis: ${fmtCurrency(assumptions.baselineNet ?? 0)} Netto je Monat`} variant="outlined"/>
                                </Stack>
                            </Box>
                        </Stack>
                    </CardContent>
                </Card>

                <Card sx={{borderRadius: 3}}>
                    <CardContent>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{mb: 2}}>
                            <Typography variant="h6">Prognostizierter Kontostand</Typography>
                            <Stack direction="row" spacing={1}>
                                <Chip label="Baseline" sx={{bgcolor: "#1976d2", color: "#fff"}}/>
                                <Chip label="What-if" sx={{bgcolor: "#ff9800", color: "#000"}}/>
                            </Stack>
                        </Stack>
                        <CashflowChart baseline={baselineSeries} adjusted={adjustedSeries} loading={loading}/>
                        <Divider sx={{my: 2}}/>
                        <Stack direction={{xs: "column", sm: "row"}} spacing={2}>
                            <Box sx={{flex: 1}}>
                                <Typography variant="body2" color="text.secondary">Startsaldo</Typography>
                                <Typography variant="h6">{fmtCurrency(forecast?.startingBalance)}</Typography>
                            </Box>
                            <Box sx={{flex: 1}}>
                                <Typography variant="body2" color="text.secondary">Saldo nach {baselineSeries.length} Monaten (Baseline)</Typography>
                                <Typography variant="h6">{fmtCurrency(endBalanceBaseline)}</Typography>
                            </Box>
                            <Box sx={{flex: 1}}>
                                <Typography variant="body2" color="text.secondary">Saldo nach What-if-Annahme</Typography>
                                <Typography variant="h6">{fmtCurrency(endBalanceAdjusted)}</Typography>
                            </Box>
                        </Stack>
                    </CardContent>
                </Card>

                <Card sx={{borderRadius: 3}}>
                    <CardContent>
                        <Typography variant="h6" sx={{mb: 2}}>Annahmen</Typography>
                        <Stack direction={{xs: "column", sm: "row"}} spacing={2}>
                            <Box sx={{flex: 1}}>
                                <Typography variant="body2" color="text.secondary">Ø historische Einnahmen</Typography>
                                <Typography variant="subtitle1">{fmtCurrency(assumptions.baselineMonthlyIncome)}</Typography>
                            </Box>
                            <Box sx={{flex: 1}}>
                                <Typography variant="body2" color="text.secondary">Ø historische Ausgaben</Typography>
                                <Typography variant="subtitle1">{fmtCurrency(assumptions.baselineMonthlyExpense)}</Typography>
                            </Box>
                            <Box sx={{flex: 1}}>
                                <Typography variant="body2" color="text.secondary">Ø wiederkehrende Einnahmen</Typography>
                                <Typography variant="subtitle1">{fmtCurrency(assumptions.recurringIncomeMonthly)}</Typography>
                            </Box>
                            <Box sx={{flex: 1}}>
                                <Typography variant="body2" color="text.secondary">Ø wiederkehrende Ausgaben</Typography>
                                <Typography variant="subtitle1">{fmtCurrency(assumptions.recurringExpenseMonthly)}</Typography>
                            </Box>
                        </Stack>
                    </CardContent>
                </Card>

                {alerts.length > 0 && (
                    <Card sx={{borderRadius: 3}}>
                        <CardContent>
                            <Typography variant="h6" sx={{mb: 2}}>Warnungen</Typography>
                            <Stack spacing={1}>
                                {alerts.map((alert) => (
                                    <Alert
                                        key={`${alert.period}-${alert.severity}`}
                                        severity={alert.severity === "CRITICAL" ? "error" : "warning"}
                                        variant="outlined"
                                    >
                                        <Stack direction={{xs: "column", sm: "row"}} spacing={1} alignItems={{sm: "center"}}>
                                            <Typography sx={{flex: 1}}>{alert.message}</Typography>
                                            <Chip label={fmtCurrency(alert.balance)} size="small"/>
                                        </Stack>
                                    </Alert>
                                ))}
                            </Stack>
                        </CardContent>
                    </Card>
                )}
            </Stack>
        </Container>
    );
}


